
/**
 * Simple request cache utility to prevent duplicate requests
 * and ensure fresh data after significant operations
 */
export const requestCache = {
  // Cache of pending requests
  _cache: new Map<string, Promise<any>>(),
  
  // Add a request to the cache
  add(key: string, promise: Promise<any>): Promise<any> {
    this._cache.set(key, promise);
    return promise;
  },
  
  // Get a request from the cache with ability to execute fetcher if not found
  get(key: string, fetchFn?: () => Promise<any>, ttl: number = 30000): Promise<any> {
    // If we have a cached request, return it
    const cachedRequest = this._cache.get(key);
    if (cachedRequest) {
      return cachedRequest;
    }
    
    // If fetchFn is provided, execute it and cache the result
    if (fetchFn) {
      const promise = fetchFn().then(result => {
        // Clear from cache once resolved to prevent stale data in future
        setTimeout(() => {
          if (this._cache.get(key) === promise) {
            this._cache.delete(key);
          }
        }, ttl); // Default TTL of 30 seconds
        
        return result;
      });
      
      // Store in cache
      this._cache.set(key, promise);
      return promise;
    }
    
    return Promise.reject(new Error(`No cached request found for key: ${key}`));
  },
  
  // Remove a request from the cache
  remove(key: string): void {
    this._cache.delete(key);
  },
  
  // Clear the entire cache
  clear(): void {
    this._cache.clear();
    console.log('Request cache cleared');
  },
  
  // Invalidate all collection-related cache entries
  invalidateCollection(collectionId: string): void {
    if (!collectionId) return;
    
    const keysToRemove: string[] = [];
    
    this._cache.forEach((_, key) => {
      if (key.includes(collectionId)) {
        keysToRemove.push(key);
      }
    });
    
    keysToRemove.forEach(key => this._cache.delete(key));
    console.log(`Invalidated ${keysToRemove.length} cache entries for collection ${collectionId}`);
  },
  
  // Invalidate all entries related to a specific content group
  invalidateContentGroup(contentId: string): void {
    if (!contentId) return;
    
    const keysToRemove: string[] = [];
    
    this._cache.forEach((_, key) => {
      if (key.includes(contentId)) {
        keysToRemove.push(key);
      }
    });
    
    keysToRemove.forEach(key => this._cache.delete(key));
    console.log(`Invalidated ${keysToRemove.length} cache entries for content group ${contentId}`);
  },
  
  // Invalidate specific query keys for React Query
  invalidateQueryKeys(queryKeys: any[][]): void {
    if (!queryKeys || !queryKeys.length) return;
    
    const keysToRemove: string[] = [];
    
    // For each query key pattern, find matching cache entries
    queryKeys.forEach(queryKey => {
      const queryKeyStr = JSON.stringify(queryKey);
      
      this._cache.forEach((_, key) => {
        // Check if the cache key contains this query key pattern
        if (key.includes(queryKeyStr) || 
            (queryKey.length === 2 && key.includes(queryKey[0]) && key.includes(queryKey[1]))) {
          keysToRemove.push(key);
        }
      });
    });
    
    // Remove all matched keys
    const uniqueKeys = [...new Set(keysToRemove)];
    uniqueKeys.forEach(key => this._cache.delete(key));
    
    if (uniqueKeys.length > 0) {
      console.log(`Invalidated ${uniqueKeys.length} cache entries for specific query keys`);
    }
  },
  
  // Force cache busting with timestamp
  forceRefresh(): string {
    return `?_t=${new Date().getTime()}`;
  },
  
  // Add supabase for backwards compatibility
  supabase: null as any
};

// Set supabase after import to avoid circular dependencies
import { supabase } from "@/integrations/supabase/client";
requestCache.supabase = supabase;
